import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { NumberInput, SelectField, SwitchField } from '../shared'
import {
    selectIBoost,
    selectSmartThermostat,
    selectBoilerManager,
    selectEVCharger,
    selectEVBatterySize,
    selectSoftGround,
    selectHardGround,
    setIBoost,
    setSmartThermostat,
    setBoilerManager,
    setEVCharger,
    setEVBatterySize,
    setSoftGround,
    setHardGround
} from '../../store/slices/addonsSlice'
import { selectEVChargers } from '../../store/slices/catalogSlice'

// Styles
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        p: 2
    },
    sectionTitle: {
        fontSize: '13px',
        fontWeight: 'bold',
        borderBottom: '1px solid #ccc',
        pb: 0.5,
        mb: 1
    },
    switchRow: {
        display: 'flex',
        gap: 3,
        flexWrap: 'wrap'
    },
    inputRow: {
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'flex-start'
    },
    evSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1
    }
}

const AddonsTab = () => {
    const dispatch = useDispatch()

    // Selectors
    const iBoost = useSelector(selectIBoost)
    const smartThermostat = useSelector(selectSmartThermostat)
    const boilerManager = useSelector(selectBoilerManager)
    const evCharger = useSelector(selectEVCharger)
    const evBatterySize = useSelector(selectEVBatterySize)
    const softGround = useSelector(selectSoftGround)
    const hardGround = useSelector(selectHardGround)
    const evChargers = useSelector(selectEVChargers)

    // Handlers
    const handleIBoostChange = (checked) => {
        dispatch(setIBoost(checked))
    }

    const handleSmartThermostatChange = (checked) => {
        dispatch(setSmartThermostat(checked))
    }

    const handleBoilerManagerChange = (checked) => {
        dispatch(setBoilerManager(checked))
    }

    const handleSoftGroundChange = (value) => {
        dispatch(setSoftGround(value))
    }

    const handleHardGroundChange = (value) => {
        dispatch(setHardGround(value))
    }

    const handleEVChargerChange = (value) => {
        const selectedCharger = evChargers.find(c => c.uid === value)
        dispatch(setEVCharger(selectedCharger || null))
    }

    const handleEVBatterySizeChange = (value) => {
        dispatch(setEVBatterySize(value))
    }

    // Format EV charger options
    const evChargerOptions = evChargers.map(charger => ({
        value: charger.uid,
        label: `${charger.manufacturer || ''} ${charger.description || charger.model || ''} ${charger.power || ''}kW`.trim()
    }))

    // Add "None" option at the beginning
    evChargerOptions.unshift({ value: '', label: 'No EV Charger' })

    return (
        <Box sx={styles.container}>
            {/* Energy Saving Devices */}
            <Box>
                <Typography sx={styles.sectionTitle}>Energy Saving Devices</Typography>
                <Box sx={styles.switchRow}>
                    <SwitchField
                        label="iBoost"
                        checked={iBoost}
                        onChange={handleIBoostChange}
                        labelPlacement="top"
                    />
                    <SwitchField
                        label="Smart Thermostat"
                        checked={smartThermostat}
                        onChange={handleSmartThermostatChange}
                        labelPlacement="top"
                    />
                    <SwitchField
                        label="Boiler Manager"
                        checked={boilerManager}
                        onChange={handleBoilerManagerChange}
                        labelPlacement="top"
                    />
                </Box>
            </Box>

            {/* Ground Works */}
            <Box>
                <Typography sx={styles.sectionTitle}>Ground Works</Typography>
                <Box sx={styles.inputRow}>
                    <NumberInput
                        label="Soft Ground"
                        value={softGround}
                        onChange={handleSoftGroundChange}
                        suffix="m"
                        decimalScale={0}
                        min={0}
                        sx={{ width: 120 }}
                    />
                    <NumberInput
                        label="Hard Ground"
                        value={hardGround}
                        onChange={handleHardGroundChange}
                        suffix="m"
                        decimalScale={0}
                        min={0}
                        sx={{ width: 120 }}
                    />
                </Box>
            </Box>

            {/* Electric Vehicle Chargers */}
            {evChargers && evChargers.length > 0 && (
                <Box>
                    <Typography sx={styles.sectionTitle}>Electric Vehicle Chargers</Typography>
                    <Box sx={styles.evSection}>
                        <SelectField
                            label="EV Charger"
                            value={evCharger?.uid || ''}
                            onChange={handleEVChargerChange}
                            options={evChargerOptions}
                            sx={{ minWidth: 280 }}
                        />
                        {evCharger && (
                            <NumberInput
                                label="EV Battery Size"
                                value={evBatterySize}
                                onChange={handleEVBatterySizeChange}
                                suffix="kWh"
                                decimalScale={0}
                                min={0}
                                sx={{ width: 150 }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default AddonsTab
